import React, { useState } from 'react';

import { getObjectSchema } from 'model/objectSchemas';
import { formatName } from 'model/nameFormatting';
import { InfoHint } from 'components/common/InfoHint';
import { toStringValue } from 'utils/strings';
import {
  StyledJsonTextarea,
  StyledFieldsetCard,
  StyledSchemaDescription,
  StyledFieldLabel,
  StyledTypeBadge,
  StyledFieldRow,
  StyledBooleanLabel,
  StyledFullWidthInput,
  StyledJsonValueTextarea,
  StyledFieldError,
} from './ObjectFieldEditor.styled';

export interface ObjectFieldEditorProps {
  objectType: string;
  value: Record<string, unknown> | undefined;
  onChange: (value: Record<string, unknown>) => void;
  nameMode: 'formatted' | 'raw';
}

interface JsonFallbackEditorProps {
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}

const JsonFallbackEditor = ({ value, onChange }: JsonFallbackEditorProps) => {
  const [text, setText] = useState<string>(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '{}';
    }
  });
  const [err, setErr] = useState<string | null>(null);

  return (
    <div>
      <StyledJsonTextarea
        value={text}
        rows={5}
        $hasError={err !== null}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value) as Record<string, unknown>;
            setErr(null);
            onChange(parsed);
          } catch (parseErr) {
            setErr(parseErr instanceof Error ? parseErr.message : 'Invalid JSON');
          }
        }}
      />
      {err && <StyledFieldError>{err}</StyledFieldError>}
    </div>
  );
};

const ObjectFieldEditor = ({ objectType, value, onChange, nameMode }: ObjectFieldEditorProps) => {
  const schema = getObjectSchema(objectType);
  const current = value ?? {};

  if (!schema) {
    return <JsonFallbackEditor value={current} onChange={onChange} />;
  }

  const updateField = (name: string, fieldValue: unknown) => {
    const next: Record<string, unknown> = { ...current };
    if (fieldValue === undefined || fieldValue === '' || fieldValue === null) {
      delete next[name];
    } else {
      next[name] = fieldValue;
    }
    onChange(next);
  };

  return (
    <StyledFieldsetCard>
      {schema.description && (
        <StyledSchemaDescription>{schema.description}</StyledSchemaDescription>
      )}
      {schema.fields.map((f) => {
        const rawValue = current[f.name];
        const label = (
          <StyledFieldLabel>
            <span>{formatName(f.name, nameMode)}</span>
            <StyledTypeBadge>
              {f.type}
              {f.type === 'array' && f.itemType ? `<${f.itemType}>` : ''}
            </StyledTypeBadge>
            {f.description && <InfoHint text={f.description} title={f.name} />}
          </StyledFieldLabel>
        );

        if (f.type === 'boolean') {
          return (
            <StyledFieldRow key={f.name}>
              <StyledBooleanLabel>
                <input
                  type='checkbox'
                  checked={Boolean(rawValue)}
                  onChange={(e) => updateField(f.name, e.target.checked)}
                />
                <span>{formatName(f.name, nameMode)}</span>
                <StyledTypeBadge>bool</StyledTypeBadge>
                {f.description && <InfoHint text={f.description} title={f.name} />}
              </StyledBooleanLabel>
            </StyledFieldRow>
          );
        }

        if (f.type === 'number' || f.type === 'integer') {
          return (
            <StyledFieldRow key={f.name}>
              {label}
              <StyledFullWidthInput
                type='number'
                value={rawValue === undefined || rawValue === null ? '' : String(rawValue)}
                onChange={(e) =>
                  updateField(f.name, e.target.value === '' ? undefined : Number(e.target.value))
                }
              />
            </StyledFieldRow>
          );
        }

        if (
          f.type === 'string-map' ||
          f.type === 'any-map' ||
          f.type === 'object' ||
          f.type === 'array'
        ) {
          return (
            <StyledFieldRow key={f.name}>
              {label}
              <StyledJsonValueTextarea
                value={(() => {
                  try {
                    return rawValue === undefined ? '' : JSON.stringify(rawValue, null, 2);
                  } catch {
                    return '';
                  }
                })()}
                rows={3}
                placeholder={f.type === 'array' ? '[]' : '{}'}
                onChange={(e) => {
                  const t = e.target.value;
                  if (!t.trim()) {
                    updateField(f.name, undefined);
                    return;
                  }

                  try {
                    updateField(f.name, JSON.parse(t));
                  } catch {
                    /* leave previous value */
                  }
                }}
              />
            </StyledFieldRow>
          );
        }

        return (
          <StyledFieldRow key={f.name}>
            {label}
            <StyledFullWidthInput
              type='text'
              value={toStringValue(rawValue)}
              onChange={(e) =>
                updateField(f.name, e.target.value === '' ? undefined : e.target.value)
              }
            />
          </StyledFieldRow>
        );
      })}
    </StyledFieldsetCard>
  );
};

const MemoObjectFieldEditor = React.memo(ObjectFieldEditor);

export { MemoObjectFieldEditor as ObjectFieldEditor };
export default MemoObjectFieldEditor;
